import { api } from './core';

export type PaymentAnomalyStatus =
  | 'refunded'
  | 'dispute_open'
  | 'dispute_closed'
  | 'failed'
  | 'canceled'
  | 'mandate_revoked'
  | 'payment_method_detached';

export interface PaymentAnomaly {
  payment_intent_id: string;
  current_status: PaymentAnomalyStatus;
  amount_cents: number | null;
  last_event_created_at: string | null;
  enrollment_id: number | null;
  last_error: string | null;
  standort_id: number | null;
  student_name: string | null;
  subprofile_id: string | null;
}

export interface PaymentAnomaliesSummary {
  openDisputes: number;
  refunds: number;
  failedPayments: number;
  paymentMethodIssues: number;
}

export interface ListAnomaliesParams {
  since?: string;
  status?: PaymentAnomalyStatus[];
  standortId?: number;
  limit?: number;
}

export type PaymentTransactionStatus =
  | 'succeeded'
  | 'processing'
  | 'requires_action'
  | 'requires_capture'
  | 'requires_payment_method'
  | 'requires_confirmation'
  | 'canceled'
  | 'failed'
  | 'refunded'
  | 'dispute_open'
  | 'dispute_closed'
  | 'mandate_revoked'
  | 'payment_method_detached'
  | 'setup_succeeded'
  | 'setup_failed';

export interface PaymentTransaction {
  payment_intent_id: string;
  object_type: string;
  current_status: PaymentTransactionStatus;
  amount_cents: number | null;
  currency: string | null;
  last_event_created_at: string | null;
  last_error: string | null;
  enrollment_id: number | null;
  wp_user_id: number | null;
  package_id: string | null;
  created_at: string;
  standort_id: number | null;
  student_name: string | null;
  fach_id: number | null;
  subprofile_id: string | null;
  payment_type: string | null;
  installments: number | null;
  package_name: string | null;
  portal_purchase_status: string | null;
  payment_method_type: string | null;
  payment_method_wallet: string | null;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
  stripe_fee_cents: number | null;
  net_amount_cents: number | null;
  stripe_fee_currency: string | null;
}

export interface RevenueBreakdown {
  byMethod: Array<{
    method_key: string;
    base_type: string | null;
    wallet: string | null;
    count: number;
    volume_cents: number;
    fee_cents: number;
    net_cents: number;
  }>;
  byMonth: Array<{
    month: string;
    volume_cents: number;
    count: number;
  }>;
  since: string;
}

export interface ForecastEntry {
  enrollment_id: number;
  due_at: string;
  standort_id: number | null;
  student_name: string | null;
  currency: string | null;
  estimated_amount_cents?: number;
  planned_amount_cents?: number;
  id?: number;
  sequence_no?: number;
  status?: string;
  payment_charge_state?: string | null;
}

export interface ForecastFollowup {
  enrollment_id: number;
  due_at: string;
  estimated_amount_cents: number;
  currency: string;
  standort_id: number | null;
  student_name: string;
  package_id: string;
  package_name: string;
  package_units: number;
  remaining_at_forecast: number;
}

export interface Forecast {
  daysAhead: number;
  until: string;
  initialCharges: ForecastEntry[];
  installments: ForecastEntry[];
  followups: ForecastFollowup[];
  summary: {
    initialCount: number;
    initialVolumeCents: number;
    installmentCount: number;
    installmentVolumeCents: number;
    followupCount: number;
    followupVolumeCents: number;
    totalCount: number;
    totalVolumeCents: number;
  };
}

// Liquiditätsprognose (services/cashflow_prognose.py): Tag für Tag bis zum Enddatum.
export type CashflowEventKind = 'income' | 'payout' | 'lessons' | 'package' | 'cancellation';
export type CashflowIncomeCategory = 'forderung' | 'beitrag' | 'rate' | 'folgepaket' | 'verbrauch';

export interface CashflowEvent {
  kind: CashflowEventKind;
  category?: CashflowIncomeCategory;
  title: string;
  detail: string;
  amountCents: number;
  count: number;
  items: { text: string; amountCents: number }[];
}

export interface CashflowDay {
  date: string;
  balanceCents: number;
  incomeCents: number;
  incomeTotalCents: number;
  liabilityCents: number;
  payableCents: number;
  payoutCents: number;
  paidTotalCents: number;
  lessons: number;
  events: CashflowEvent[];
}

export interface CashflowForecast {
  from: string;
  until: string;
  payoutDay: number;
  startBalanceCents: number;
  days: CashflowDay[];
  summary: {
    startBalanceCents: number;
    endBalanceCents: number;
    minBalanceCents: number;
    minBalanceDate: string;
    incomeTotalCents: number;
    paidTotalCents: number;
    startPayableCents: number;
    startLiabilityCents: number;
    endLiabilityCents: number;
    endPayableCents: number;
    overdueCents: number;
    lessonCount: number;
    lessonsWithoutRate: number;
    followupPackages: number;
    nextPayoutDate: string;
    incomeByCategory: Record<CashflowIncomeCategory, number>;
  };
  assumptions: string[];
}

export interface PaymentSummary {
  totalCount: number;
  succeededCount: number;
  processingCount: number;
  failedCount: number;
  refundedCount: number;
  disputeOpenCount: number;
  succeededVolumeCents: number;
  refundedVolumeCents: number;
  failedVolumeCents: number;
  succeededFeeCents: number;
  succeededNetCents: number;
}

export interface WebhookEvent {
  id: number;
  event_id: string;
  event_type: string;
  livemode: number;
  event_created_at: string | null;
  is_processed: number;
  processed_at: string | null;
  processing_error: string | null;
  created_at: string;
}

export interface LedgerEntry {
  id: number;
  entry_type: string;
  units: number;
  purchase_total_cents: number | null;
  currency: string | null;
  source_type: string;
  balance_after: number | null;
  meta_json: string | null;
  created_at: string;
  account_id: number;
}

export interface InstallmentInfo {
  id: number;
  sequence_no: number;
  planned_amount_cents: number;
  due_at: string;
  status: string;
  actual_charged_cents: number | null;
  coupon_applied_cents: number;
  retry_count: number;
  last_error: string | null;
  last_attempt_at: string | null;
}

export interface TransactionDetail {
  transaction: PaymentTransaction & {
    metadata_json: string | null;
    contingent_package_id: string | null;
    enrollment_currency: string | null;
    package_units: number | null;
    fulfilled_at: string | null;
    failure_reason: string | null;
  };
  webhookEvents: WebhookEvent[];
  ledgerEntries: LedgerEntry[];
  installment: InstallmentInfo | null;
}

export interface ListTransactionsParams {
  since?: string;
  status?: PaymentTransactionStatus[];
  standortId?: number;
  search?: string;
  limit?: number;
  offset?: number;
  wpUserId?: number;
}

export const paymentsApi = {
  listAnomalies: async (params: ListAnomaliesParams = {}): Promise<{ rows: PaymentAnomaly[] }> => {
    const qp = new URLSearchParams();
    if (params.since) qp.set('since', params.since);
    if (params.standortId && params.standortId > 0) qp.set('standort_id', String(params.standortId));
    if (params.limit) qp.set('limit', String(params.limit));
    if (params.status?.length) {
      for (const s of params.status) qp.append('status[]', s);
    }
    const url = '/api/payments/anomalies' + (qp.toString() ? `?${qp}` : '');
    return api.get<{ rows: PaymentAnomaly[] }>(url);
  },

  anomaliesSummary: async (params: { standortId?: number; since?: string } = {}): Promise<PaymentAnomaliesSummary> => {
    const qp = new URLSearchParams();
    if (params.since) qp.set('since', params.since);
    if (params.standortId && params.standortId > 0) qp.set('standort_id', String(params.standortId));
    const url = '/api/payments/anomalies/summary' + (qp.toString() ? `?${qp}` : '');
    return api.get<PaymentAnomaliesSummary>(url);
  },

  listTransactions: async (
    params: ListTransactionsParams = {},
  ): Promise<{ rows: PaymentTransaction[]; total: number; limit: number; offset: number }> => {
    const qp = new URLSearchParams();
    if (params.since) qp.set('since', params.since);
    if (params.standortId && params.standortId > 0) qp.set('standort_id', String(params.standortId));
    if (params.wpUserId && params.wpUserId > 0) qp.set('wp_user_id', String(params.wpUserId));
    if (params.limit) qp.set('limit', String(params.limit));
    if (params.offset) qp.set('offset', String(params.offset));
    if (params.search) qp.set('search', params.search);
    if (params.status?.length) {
      for (const s of params.status) qp.append('status[]', s);
    }
    const url = '/api/payments/transactions' + (qp.toString() ? `?${qp}` : '');
    return api.get<{ rows: PaymentTransaction[]; total: number; limit: number; offset: number }>(url);
  },

  summary: async (params: { standortId?: number; since?: string } = {}): Promise<PaymentSummary> => {
    const qp = new URLSearchParams();
    if (params.since) qp.set('since', params.since);
    if (params.standortId && params.standortId > 0) qp.set('standort_id', String(params.standortId));
    const url = '/api/payments/summary' + (qp.toString() ? `?${qp}` : '');
    return api.get<PaymentSummary>(url);
  },

  transactionDetail: async (paymentIntentId: string): Promise<TransactionDetail> => {
    return api.get<TransactionDetail>(`/api/payments/transactions/${encodeURIComponent(paymentIntentId)}`);
  },

  syncFromStripe: async (params: { since?: string; maxPages?: number } = {}): Promise<{ ok: boolean; synced: number; failed: number; since: string }> => {
    return api.post<{ ok: boolean; synced: number; failed: number; since: string }>('/api/payments/sync', params);
  },

  revenueBreakdown: async (params: { standortId?: number; since?: string } = {}): Promise<RevenueBreakdown> => {
    const qp = new URLSearchParams();
    if (params.since) qp.set('since', params.since);
    if (params.standortId && params.standortId > 0) qp.set('standort_id', String(params.standortId));
    const url = '/api/payments/revenue-breakdown' + (qp.toString() ? `?${qp}` : '');
    return api.get<RevenueBreakdown>(url);
  },

  forecast: async (params: { standortId?: number; daysAhead?: number } = {}): Promise<Forecast> => {
    const qp = new URLSearchParams();
    if (params.daysAhead) qp.set('days_ahead', String(params.daysAhead));
    if (params.standortId && params.standortId > 0) qp.set('standort_id', String(params.standortId));
    const url = '/api/payments/forecast' + (qp.toString() ? `?${qp}` : '');
    return api.get<Forecast>(url);
  },

  cashflowForecast: async (params: {
    until: string;
    standortId?: number;
    payoutDay?: number;
    startBalanceCents?: number;
  }): Promise<CashflowForecast> => {
    const qp = new URLSearchParams({ until: params.until });
    if (params.standortId && params.standortId > 0) qp.set('standort_id', String(params.standortId));
    if (params.payoutDay) qp.set('payout_day', String(params.payoutDay));
    if (params.startBalanceCents) qp.set('start_balance_cents', String(params.startBalanceCents));
    return api.get<CashflowForecast>(`/api/payments/cashflow-forecast?${qp}`);
  },
};

// ──────────────────── Label-Helfer ────────────────────

const PM_TYPE_LABEL: Record<string, string> = {
  card: 'Karte',
  sepa_debit: 'SEPA-Lastschrift',
  bacs_debit: 'BACS-Lastschrift',
  au_becs_debit: 'BECS-Lastschrift (AU)',
  link: 'Stripe Link',
  klarna: 'Klarna',
  afterpay_clearpay: 'Afterpay/Clearpay',
  paypal: 'PayPal',
  giropay: 'giropay',
  sofort: 'Sofort',
  ideal: 'iDEAL',
  bancontact: 'Bancontact',
  eps: 'EPS',
  p24: 'Przelewy24',
  us_bank_account: 'US-Bank',
};

const PM_WALLET_LABEL: Record<string, string> = {
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  samsung_pay: 'Samsung Pay',
  link: 'Stripe Link',
  amex_express_checkout: 'Amex Express Checkout',
  masterpass: 'Masterpass',
  visa_checkout: 'Visa Checkout',
};

/**
 * Kombiniertes Label für eine Zahlungsmethode:
 *   card + wallet=apple_pay → "Apple Pay"
 *   card + brand=visa + last4=4242 → "Karte · Visa •••• 4242"
 *   sepa_debit + last4=3000 → "SEPA-Lastschrift •••• 3000"
 *   klarna → "Klarna"
 */
export function formatPaymentMethod(tx: {
  payment_method_type: string | null;
  payment_method_wallet: string | null;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}): string {
  const type = tx.payment_method_type || '';
  const wallet = tx.payment_method_wallet || '';
  const brand = tx.payment_method_brand || '';
  const last4 = tx.payment_method_last4 || '';

  // Wallet-Subtyp hat Priorität (Apple Pay, Google Pay usw.)
  if (wallet && PM_WALLET_LABEL[wallet]) {
    const w = PM_WALLET_LABEL[wallet];
    return last4 ? `${w} •••• ${last4}` : w;
  }
  if (!type) return '–';

  const typeLabel = PM_TYPE_LABEL[type] || type;
  if (type === 'card' && brand) {
    const brandCapital = brand.charAt(0).toUpperCase() + brand.slice(1);
    return last4 ? `${typeLabel} · ${brandCapital} •••• ${last4}` : `${typeLabel} · ${brandCapital}`;
  }
  if (last4) {
    return `${typeLabel} •••• ${last4}`;
  }
  return typeLabel;
}

/**
 * True, wenn die Zahlungsmethode async ist (SEPA, Klarna, Sofort, giropay)
 * und daher Tage bis zur finalen Bestätigung dauern kann.
 */
export function isAsyncPaymentMethod(type: string | null): boolean {
  if (!type) return false;
  return ['sepa_debit', 'bacs_debit', 'au_becs_debit', 'klarna', 'afterpay_clearpay', 'sofort', 'giropay', 'ideal', 'bancontact', 'p24', 'eps', 'us_bank_account'].includes(type);
}
